import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { notify } from '../../common/functions/react-toastify.function';
import FileUploadSection from '../VendorInvoiceManagement/components/file_upload_section.component';
// eslint-disable-next-line max-len
import MultipleFileUploadSection from '../VendorInvoiceManagement/components/multiple_file_upload_section.component';
import {
  getInvoiceAttachments,
  searchInvoiceAttachments,
  uploadInvoiceOtherDocuments,
  uploadInvoicePoFile,
} from './invoice_attachment_management.api';

const OTHER_DOCUMENT_EXTENSIONS = ['.pdf', '.xls', '.xlsx', '.kmz', '.msg'];
const OTHER_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.google-earth.kmz',
  'application/vnd.google-earth.kmz+xml',
  'application/vnd.ms-outlook',
  'message/rfc822',
];

const getFileName = (filePath) => {
  if (!filePath) {
    return '';
  }
  const parts = String(filePath).split('/');
  return parts[parts.length - 1];
};

const getUploadPercentage = (event) => {
  if (!event.total) {
    return 0;
  }
  return Math.min(100, Math.round((event.loaded * 100) / event.total));
};

export const InvoiceAttachmentManagement = () => {
  const navigate = useNavigate();
  const hasAdminAccess = useMemo(() => {
    try {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
      return (
        Array.isArray(permissions) &&
        permissions.some((permission) => permission.name === 'Invoice Attachment Management')
      );
    } catch {
      return false;
    }
  }, []);
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [poFile, setPoFile] = useState(null);
  const [otherFiles, setOtherFiles] = useState([]);
  const [poUploading, setPoUploading] = useState(false);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [poProgress, setPoProgress] = useState(0);
  const [documentsProgress, setDocumentsProgress] = useState(0);

  useEffect(() => {
    if (!hasAdminAccess) {
      navigate('/rov', { replace: true });
    }
  }, [hasAdminAccess, navigate]);

  useEffect(() => {
    if (!hasAdminAccess) {
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(
      async () => {
        setLoadingOptions(true);
        try {
          const results = await searchInvoiceAttachments(searchText);
          if (active) {
            setOptions(results);
          }
        } catch {
          if (active) {
            setOptions([]);
          }
        } finally {
          if (active) {
            setLoadingOptions(false);
          }
        }
      },
      searchText ? 350 : 0
    );

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [hasAdminAccess, searchText]);

  const existingDocuments = useMemo(
    () => (Array.isArray(invoice?.other_documents_paths) ? invoice.other_documents_paths : []),
    [invoice]
  );

  const updateInvoiceState = (updatedInvoice) => {
    setInvoice(updatedInvoice);
    setSelectedOption(updatedInvoice);
    setOptions((current) =>
      current.map((item) => (item.id === updatedInvoice.id ? updatedInvoice : item))
    );
  };

  const handleInvoiceSelection = async (_, value) => {
    setSelectedOption(value);
    setInvoice(null);
    setPoFile(null);
    setOtherFiles([]);
    if (!value) {
      return;
    }

    setLoadingInvoice(true);
    try {
      const details = await getInvoiceAttachments(value.id);
      setInvoice(details);
    } catch {
      setSelectedOption(null);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handlePoUpload = async () => {
    if (!invoice?.id || !poFile || poUploading) {
      return;
    }

    setPoUploading(true);
    setPoProgress(0);
    try {
      const updatedInvoice = await uploadInvoicePoFile(invoice.id, poFile, (event) =>
        setPoProgress(getUploadPercentage(event))
      );
      updateInvoiceState(updatedInvoice);
      setPoFile(null);
      setPoProgress(100);
      notify('success', 'PO file uploaded successfully');
    } catch {
      setPoProgress(0);
    } finally {
      setPoUploading(false);
    }
  };

  const handleDocumentsUpload = async () => {
    if (!invoice?.id || otherFiles.length === 0 || documentsUploading) {
      return;
    }

    setDocumentsUploading(true);
    setDocumentsProgress(0);
    try {
      const updatedInvoice = await uploadInvoiceOtherDocuments(invoice.id, otherFiles, (event) =>
        setDocumentsProgress(getUploadPercentage(event))
      );
      updateInvoiceState(updatedInvoice);
      setOtherFiles([]);
      setDocumentsProgress(100);
      notify('success', 'Documents uploaded successfully');
    } catch {
      setDocumentsProgress(0);
    } finally {
      setDocumentsUploading(false);
    }
  };

  return (
    <DashboardLayout padding>
      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, md: 4 }, pb: 6 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Button
            aria-label="Back to invoice management"
            onClick={() => navigate('/rov', { state: { openLandingInvoiceManagement: true } })}
            sx={{ minWidth: 40, width: 40, height: 40, borderRadius: 2, color: '#BD1D23' }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#202124' }}>
              Invoice Attachment Management
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
              Add missing PO files and supporting documents to existing invoices.
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{ p: { xs: 2, md: 3 }, mb: 3, border: '1px solid #e5e7eb', borderRadius: 3 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 650, mb: 0.5 }}>
            Select invoice
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            Search by invoice number, PO number, or vendor.
          </Typography>
          <Autocomplete
            value={selectedOption}
            options={options}
            loading={loadingOptions}
            filterOptions={(items) => items}
            getOptionLabel={(option) => option?.invoice_number || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleInvoiceSelection}
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setSearchText(value);
              }
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{option.invoice_number}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    PO: {option.po_number || 'Not available'} ·{' '}
                    {option.vendor || 'Vendor not available'}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Invoice"
                placeholder="Start typing an invoice number"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Paper>

        {loadingInvoice && (
          <Paper
            elevation={0}
            sx={{ p: 5, textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 3 }}
          >
            <CircularProgress size={32} />
          </Paper>
        )}

        {!loadingInvoice && !invoice && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select an invoice to view and manage its attachments.
          </Alert>
        )}

        {!loadingInvoice && invoice && (
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{ p: { xs: 2, md: 3 }, border: '1px solid #e5e7eb', borderRadius: 3 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Invoice number
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {invoice.invoice_number}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    PO {invoice.po_number || 'Not available'} ·{' '}
                    {invoice.vendor || 'Vendor not available'}
                  </Typography>
                </Box>
                <Chip label={invoice.status || 'Unknown status'} sx={{ alignSelf: 'flex-start' }} />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{ p: { xs: 2, md: 3 }, border: '1px solid #e5e7eb', borderRadius: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <UploadFileOutlinedIcon sx={{ color: '#BD1D23' }} />
                <Typography variant="h6" sx={{ fontWeight: 650 }}>
                  PO File
                </Typography>
              </Stack>
              <Divider sx={{ my: 2 }} />

              {invoice.po_file_path ? (
                <Alert
                  icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                  severity="success"
                  sx={{ alignItems: 'center' }}
                >
                  PO file already exists: <strong>{getFileName(invoice.po_file_path)}</strong>
                </Alert>
              ) : (
                <>
                  <FileUploadSection
                    description="Choose or drop the PO file"
                    downloadLinkText="PDF only · Maximum 50 MB"
                    file={poFile}
                    onFileChange={setPoFile}
                    isLoading={poUploading}
                    loadingText="Uploading PO file..."
                  />
                  {poUploading && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress variant="determinate" value={poProgress} />
                      <Typography variant="caption" color="text.secondary">
                        {poProgress}% uploaded
                      </Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<UploadFileOutlinedIcon />}
                    disabled={!poFile || poUploading}
                    onClick={handlePoUpload}
                  >
                    Upload PO File
                  </Button>
                </>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{ p: { xs: 2, md: 3 }, border: '1px solid #e5e7eb', borderRadius: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <DescriptionOutlinedIcon sx={{ color: '#BD1D23' }} />
                <Typography variant="h6" sx={{ fontWeight: 650 }}>
                  Other Documents
                </Typography>
              </Stack>
              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Existing documents ({existingDocuments.length})
              </Typography>
              {existingDocuments.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                  {existingDocuments.map((documentPath, index) => (
                    <Chip
                      key={`${documentPath}-${index}`}
                      icon={<DescriptionOutlinedIcon />}
                      label={getFileName(documentPath)}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  No supporting documents have been uploaded yet.
                </Typography>
              )}

              <MultipleFileUploadSection
                description="Choose or drop supporting documents"
                downloadLinkText="PDF, XLS, XLSX, KMZ, and MSG · Maximum 50 MB per file"
                files={otherFiles}
                onFilesChange={setOtherFiles}
                allowedExtensions={OTHER_DOCUMENT_EXTENSIONS}
                allowedMimeTypes={OTHER_DOCUMENT_MIME_TYPES}
                allowedTypesLabel="PDF, XLS, XLSX, KMZ, or MSG"
                accept=".pdf,.xls,.xlsx,.kmz,.msg"
                disabled={documentsUploading}
              />
              {documentsUploading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={documentsProgress} />
                  <Typography variant="caption" color="text.secondary">
                    {documentsProgress}% uploaded
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                startIcon={<UploadFileOutlinedIcon />}
                disabled={otherFiles.length === 0 || documentsUploading}
                onClick={handleDocumentsUpload}
              >
                Upload Documents
              </Button>
            </Paper>
          </Stack>
        )}
      </Box>
    </DashboardLayout>
  );
};
